// photoNames.js — People_Photos file-name rules. Pure: no I/O, no React, no
// Graph. contactsApi.js re-exports these; nothing else may hold a copy.
//
//   main photo      P-NNNNN.jpg          chosen by People.PhotoRef, NEVER by name
//   extra photos    P-NNNNN__1.jpg, P-NNNNN__alt2.png, P-NNNNN__<anything>.jpeg
//
// A person's gallery is every library file whose name matches photoNameRe();
// which of them is "main" is whatever PhotoRef says.

export const PHOTO_MAX_BYTES = 20 * 1024 * 1024;        // refuse above this, plainly
export const SIMPLE_PUT_LIMIT = 4 * 1024 * 1024;        // Graph's single-shot PUT ceiling
export const UPLOAD_CHUNK = 16 * 320 * 1024;            // 5 MiB — session chunks must be a multiple of 320 KiB

const escapeRe = s => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** ^<personId>(__[A-Za-z0-9]+)?\.(jpg|jpeg|png)$, case-insensitive. P-00035 does not match P-000351.jpg. */
export function photoNameRe(personId) {
  return new RegExp(`^${escapeRe(personId)}(__[A-Za-z0-9]+)?\\.(jpg|jpeg|png)$`, 'i');
}

const suffixNumber = suffix => { const m = /^__(?:alt)?(\d+)$/i.exec(suffix || ''); return m ? Number(m[1]) : Infinity; };

/**
 * Every library file that belongs to the person: the unsuffixed name first,
 * then __N / __altN by number, then any other suffix by name. This is the
 * display order only — main is whatever PhotoRef says.
 */
export function galleryNames(photoMap, personId) {
  if (!personId || !photoMap) return [];
  const re = photoNameRe(personId);
  const key = n => { const m = re.exec(n); return m[1] ? [1, suffixNumber(m[1]), n.toLowerCase()] : [0, 0, n.toLowerCase()]; };
  return [...photoMap.keys()].filter(n => re.test(n))
    .sort((a, b) => { const ka = key(a), kb = key(b); return ka[0] - kb[0] || ka[1] - kb[1] || (ka[2] < kb[2] ? -1 : ka[2] > kb[2] ? 1 : 0); });
}

/**
 * The next free extra-photo name: __N where N is 1 + the highest number among
 * the person's existing __N and __altN suffixes (1 when there are none). The
 * main file and non-numeric suffixes do not count.
 */
export function nextPhotoName(personId, existingNames, ext) {
  if (ext !== 'jpg' && ext !== 'png') throw new Error(`Unsupported photo extension: ${ext}`);
  const re = photoNameRe(personId);
  let max = 0;
  for (const n of existingNames || []) {
    const m = re.exec(n);
    if (!m || !m[1]) continue;
    const num = /^__(?:alt)?(\d+)$/i.exec(m[1]);
    if (num) max = Math.max(max, Number(num[1]));
  }
  return `${personId}__${max + 1}.${ext}`;
}

/** 'jpg' | 'png' | null from the first bytes. FF D8 FF is JPEG, 89 50 4E 47 is PNG; a HEIC (....ftyp) is neither. */
export function sniffImageType(bytes) {
  const b = bytes || [];
  if (b.length >= 3 && b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'jpg';
  if (b.length >= 4 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return 'png';
  return null;
}
